/**
 * Keep the client-side checklist and the server-side policy validator aligned
 * by deriving both from the same password rule definitions.
 */
export const PASSWORD_MIN_LENGTH = 14;
export const PASSWORD_ALLOWED_SPECIAL_CHARACTERS = '#?!@$%^&*-_';
export const PASSWORD_RESTRICTED_PATTERN_EXAMPLES = [
  '1234',
  'test',
  'pass',
  'minerva',
] as const;

const PASS_UPPERCASE_RG = /[A-Z]/;
const PASS_LOWERCASE_RG = /[a-z]/;
const PASS_NUMERIC_RG = /[0-9]/;
const PASS_SPECIAL_CHARACTER_RG = /[#?!@$%^&*_-]/;
const PASS_INVALID_CHAR_RG = /[\r\n\t]/;
const PASS_INVALID_RG = /pass|1234|test|minerva/i;

export class PasswordValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PasswordValidationError';
  }
}

export function getPasswordRuleChecks(password = '') {
  return {
    hasAllowedSpecialCharacter: PASS_SPECIAL_CHARACTER_RG.test(password),
    hasInvalidWhitespace: PASS_INVALID_CHAR_RG.test(password),
    hasLowercase: PASS_LOWERCASE_RG.test(password),
    hasMinimumLength: password.length >= PASSWORD_MIN_LENGTH,
    hasNumeric: PASS_NUMERIC_RG.test(password),
    hasRestrictedPatterns: PASS_INVALID_RG.test(password),
    hasUppercase: PASS_UPPERCASE_RG.test(password),
  };
}

function hasRequiredCharacterMix(password: string) {
  const checks = getPasswordRuleChecks(password);

  return (
    checks.hasUppercase &&
    checks.hasLowercase &&
    checks.hasNumeric &&
    checks.hasAllowedSpecialCharacter
  );
}

/**
 * Perform a check on a password string to ensure
 * that it conforms with the MinervaAI password policy.
 *
 * The requirements are:
 *   - At least 14 characters in length
 *   - Combination of uppercase and lowercase characters
 *   - At least one number
 *   - At least one special character
 *   - No dictionary words, no "pass" or "1234"
 *
 * @param password The password string to validate.
 *
 * @throws {Error} If:
 *   - The password parameter is missing
 *   - Any of the requirement checks fail
 *
 * @returns If validation passes.
 */

export const validatePassword = (password?: string) => {
  if (!password) {
    throw new PasswordValidationError('Missing required password parameter');
  }

  if (password.length < PASSWORD_MIN_LENGTH) {
    throw new PasswordValidationError(
      `The password must be at least ${PASSWORD_MIN_LENGTH} characters in length`
    );
  }

  if (!hasRequiredCharacterMix(password)) {
    throw new PasswordValidationError(
      `The password must be a combination of uppercase and lowercase characters, numbers, and special characters (only including the following: ${PASSWORD_ALLOWED_SPECIAL_CHARACTERS}).`
    );
  }

  if (PASS_INVALID_CHAR_RG.test(password)) {
    throw new PasswordValidationError(
      'The password cannot contain invalid whitespace or newline characters'
    );
  }

  if (PASS_INVALID_RG.test(password)) {
    throw new PasswordValidationError(
      'The password cannot contain commonly hacked/insecure phrases in it such as "test", "pass", or "1234"'
    );
  }
};
